// Wake screen + keep-awake logic (no React here, so it is unit-testable).
//
// Every tenant, prod included, scales to zero when idle (.github/fleet/fleet.json, Aaron 2026-09-26), so an
// idle environment costs ~nothing but the first request after a quiet spell waits out a 30-90s backend boot.
// WakeGate shows a screen that says so, then offers "Keep awake for 1h / 2h / 4h", which asks the backend
// (/v6/public/keepwarm, app/keepwarm.py) to keep itself warm until then. Demo tenants get "demo" wording;
// prod tenants get the same explanation without it (Aaron 2026-09-27: "Offer it on prod too").
//
// Uses fetch, not axios: the axios interceptors add auth, retries and the DB-warming banner, none of
// which a pre-sign-in liveness probe wants.

export const FIRST_WAIT_MS = 3000; // answer inside this and the screen never shows
export const POLL_TIMEOUT_MS = 15000;
export const POLL_GAP_MS = 2000;
export const READY_AUTO_CONTINUE_S = 20;
export const KEEP_AWAKE_HOURS = [1, 2, 4] as const;

/**
 * Demo tenant or paying one: decides the wording only. The tenant's own `demo` flag (src/config/product.ts)
 * wins; without one, the .aeronet.dev zone is demo and .aeronet.app is prod. Mustang is the case the suffix
 * gets wrong: a demo tenant on the .app zone.
 */
export function isDemoTenant(hostname: string, configDemo?: boolean): boolean {
  return configDemo ?? hostname.endsWith(".aeronet.dev");
}

/** No gate on local dev (relative API base) or in tests. */
export function shouldSkip(apiBase: string, hostname: string, nodeEnv?: string): boolean {
  if (nodeEnv === "test") return true;
  if (!apiBase) return true;
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".localhost");
}

/**
 * Fire-and-forget GET <api>/health at page load, so a cold backend boots while the user signs in. Shows
 * nothing: the wake screen itself waits for sign-in (WakeGate `signedIn`). keepalive lets it outlive the
 * Cognito hosted-UI redirect.
 */
export function prewarm(apiBase: string, fetchImpl: typeof fetch = fetch): void {
  if (!apiBase) return;
  fetchImpl(`${apiBase}/health`, { cache: "no-store", keepalive: true }).catch(() => {});
}

/**
 * GET <api>/health. True once the backend ANSWERS: 200, or 404 from an image that predates /health.
 * A 5xx (the load balancer while the container boots), a timeout or a network error means still waking.
 */
export async function probe(apiBase: string, timeoutMs: number, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetchImpl(`${apiBase}/health`, { signal: ctl.signal, cache: "no-store" });
    return r.status === 200 || r.status === 404;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export type KeepWarmStatus = { enabled: boolean; until?: string | null; maxHours?: number; message?: string };

export async function keepWarmStatus(apiBase: string, fetchImpl: typeof fetch = fetch): Promise<KeepWarmStatus> {
  try {
    const r = await fetchImpl(`${apiBase}/v6/public/keepwarm`, { cache: "no-store" });
    if (r.status === 503) return { enabled: true, message: (await r.json()).message };
    if (!r.ok) return { enabled: false };
    const j = await r.json();
    return { enabled: !!j.enabled, until: j.until ?? null, maxHours: j.max_hours };
  } catch {
    return { enabled: false };
  }
}

export async function keepAwake(apiBase: string, hours: number, fetchImpl: typeof fetch = fetch): Promise<KeepWarmStatus> {
  try {
    const r = await fetchImpl(`${apiBase}/v6/public/keepwarm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hours }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) return { enabled: r.status !== 404, message: j.message || `Keep-awake failed (HTTP ${r.status}).` };
    return { enabled: true, until: j.until ?? null };
  } catch {
    return { enabled: true, message: "Keep-awake request did not reach the server." };
  }
}

/** "3:40 pm" in the viewer's locale, or "" for a missing/invalid time. */
export function formatUntil(iso?: string | null, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}
